import { t } from "../../i18n";
import { ChevronDown, ChevronUp, Grid2X2, Languages, List, Pin, RectangleHorizontal, SquareCheck } from "lucide-react";
import { useState } from "react";
import { LanguageSelector } from "../LanguageSelector";
import { SettingsToggle } from "./SettingsToggle";
import type {
  HistoryExpansionMode,
  CloseTableButtonStyle,
  ProductDisplayMode,
  ProductScrollMode,
  StickyKey
} from "../../storage";
import {
  loadDetailsPromptOnAdd,
  loadCloseTableButtonStyle,
  loadHistoryExpansionMode,
  loadKeepUnsentDraft,
  loadProductDisplayMode,
  loadProductImagesVisible,
  loadProductScrollMode,
  loadSticky,
  saveDetailsPromptOnAdd,
  saveCloseTableButtonStyle,
  saveHistoryExpansionMode,
  saveKeepUnsentDraft,
  saveProductDisplayMode,
  saveProductImagesVisible,
  saveProductScrollMode,
  saveSticky
} from "../../storage";

type Props = {
  back: React.ReactNode;
};

const STICKY_ROWS: { key: StickyKey; label: string }[] = [
  { key: "categories", label: "Σταθερές κατηγορίες προϊόντων" },
  { key: "actions", label: "Σταθερά κουμπιά αποστολής / κλεισίματος" },
  { key: "tabs", label: "Σταθερές καρτέλες Προϊόντα / Παραγγελία" },
  { key: "takeaway", label: "Σταθερή επιλογή Πακέτο" },
  { key: "comment", label: "Σταθερό πεδίο σχολίου προϊόντος" }
];

export function UiPage({ back }: Props) {
  const [scrollMode, setScrollMode] = useState<ProductScrollMode>(loadProductScrollMode);
  const [displayMode, setDisplayMode] = useState<ProductDisplayMode>(loadProductDisplayMode);
  const [imagesVisible, setImagesVisible] = useState(loadProductImagesVisible);
  const [historyExpansion, setHistoryExpansion] = useState<HistoryExpansionMode>(
    loadHistoryExpansionMode
  );
  const [detailsPrompt, setDetailsPrompt] = useState(loadDetailsPromptOnAdd);
  const [keepUnsentDraft, setKeepUnsentDraft] = useState(loadKeepUnsentDraft);
  const [closeButtonStyle, setCloseButtonStyle] = useState<CloseTableButtonStyle>(
    loadCloseTableButtonStyle
  );
  const [sticky, setSticky] = useState<Record<StickyKey, boolean>>(() => ({
    categories: loadSticky("categories"),
    actions: loadSticky("actions"),
    tabs: loadSticky("tabs"),
    takeaway: loadSticky("takeaway"),
    comment: loadSticky("comment")
  }));

  const toggle = (key: StickyKey): void => {
    const next = !sticky[key];
    setSticky({ ...sticky, [key]: next });
    saveSticky(key, next);
  };

  return (
    <section className="screen settings-screen appearance-screen" aria-labelledby="settings-heading">
      {back}
      <div className="screen-heading-row">
        <div>
          <h1 id="settings-heading">{t("Εμφάνιση")}</h1>
          <p>{t("Κύλιση και διάταξη")}</p>
        </div>
        <p className="settings-autosave"><SquareCheck aria-hidden="true" />{t("Αυτόματη αποθήκευση")}</p>
      </div>

      <div className="settings-groups appearance-groups">
        <section className="settings-group appearance-wide appearance-language-row">
          <div>
            <h2><Languages aria-hidden="true" />{t("Γλώσσα εφαρμογής")}</h2>
            <p className="appearance-description">{t("Η γλώσσα αλλάζει μόνο τα κουμπιά και τις οδηγίες. Τα δεδομένα της επιχείρησης και τα δελτία εκτύπωσης παραμένουν όπως τα έχετε ορίσει.")}</p>
          </div>
          <LanguageSelector compact hideLabel />
        </section>
        <section className="settings-group">
          <h2>{t("Εμφάνιση προϊόντων στο τραπέζι")}</h2>
          <div className="segmented-control two-options" role="group" aria-label={t("Τρόπος εμφάνισης προϊόντων")}>
            <button
              type="button"
              className={displayMode === "list" ? "active" : ""}
              aria-pressed={displayMode === "list"}
              onClick={() => {
                setDisplayMode("list");
                saveProductDisplayMode("list");
              }}
            >
              <List />
              <span>{t("Λίστα")}</span>
            </button>
            <button
              type="button"
              className={displayMode === "tiles" ? "active" : ""}
              aria-pressed={displayMode === "tiles"}
              onClick={() => {
                setDisplayMode("tiles");
                saveProductDisplayMode("tiles");
              }}
            >
              <Grid2X2 />
              <span>{t("Πλακίδια")}</span>
            </button>
          </div>
          <SettingsToggle
            label={t("Εικόνες στα πλακίδια")}
            checked={imagesVisible}
            onChange={value => { setImagesVisible(value); saveProductImagesVisible(value); }}
          />
        </section>

        <section className="settings-group">
          <h2>{t("Λίστα προϊόντων στην παραγγελία")}</h2>
          <label className="field">
            <select
              value={scrollMode}
              onChange={(event) => {
                const mode = event.target.value as ProductScrollMode;
                setScrollMode(mode);
                saveProductScrollMode(mode);
              }}
              aria-label={t("Λίστα προϊόντων στην παραγγελία")}
            >
              <option value="inner">{t("Εσωτερική κύλιση")}</option>
              <option value="page">{t("Κύλιση με τη σελίδα")}</option>
            </select>
          </label>
        </section>

        <section className="settings-group appearance-wide">
          <h2>{t("Παραγγελία")}</h2>
          <SettingsToggle
            label={t("Άνοιγμα καρτέλας Χωρίς / Extra σε κάθε προσθήκη")}
            description={t("Όταν είναι κλειστό, το προϊόν μπαίνει κατευθείαν στην παραγγελία και η καρτέλα ανοίγει μόνο με το μολύβι.")}
            checked={detailsPrompt}
            onChange={value => { setDetailsPrompt(value); saveDetailsPromptOnAdd(value); }}
          />
          <SettingsToggle
            label={t("Διατήρηση προϊόντων που δεν στάλθηκαν")}
            description={t("Όταν είναι κλειστό, όσα προϊόντα δεν έχουν σταλεί διαγράφονται μόλις βγεις από το τραπέζι.")}
            checked={keepUnsentDraft}
            onChange={value => { setKeepUnsentDraft(value); saveKeepUnsentDraft(value); }}
          />
        </section>

        <section className="settings-group">
          <h2>{t("Αρχική κατάσταση ιστορικού")}</h2>
          <div className="segmented-control two-options" role="group" aria-label={t("Αρχική κατάσταση καρτών ιστορικού")}>
            <button
              type="button"
              className={historyExpansion === "open" ? "active" : ""}
              aria-pressed={historyExpansion === "open"}
              onClick={() => {
                setHistoryExpansion("open");
                saveHistoryExpansionMode("open");
              }}
            >
              <ChevronUp />
              <span>{t("Ανοιχτές")}</span>
            </button>
            <button
              type="button"
              className={historyExpansion === "closed" ? "active" : ""}
              aria-pressed={historyExpansion === "closed"}
              onClick={() => {
                setHistoryExpansion("closed");
                saveHistoryExpansionMode("closed");
              }}
            >
              <ChevronDown />
              <span>{t("Κλειστές")}</span>
            </button>
          </div>
        </section>

        <section className="settings-group">
          <h2>{t("Κουμπί κλεισίματος τραπεζιού")}</h2>
          <div className="segmented-control two-options" role="group" aria-label={t("Μορφή κουμπιού κλεισίματος τραπεζιού")}>
            <button
              type="button"
              className={closeButtonStyle === "compact" ? "active" : ""}
              aria-pressed={closeButtonStyle === "compact"}
              aria-label={t("Τετράγωνο ✓")}
              onClick={() => {
                setCloseButtonStyle("compact");
                saveCloseTableButtonStyle("compact");
              }}
            >
              <SquareCheck aria-hidden="true" />
              <span>{t("Τετράγωνο")}</span>
            </button>
            <button
              type="button"
              className={closeButtonStyle === "full" ? "active" : ""}
              aria-pressed={closeButtonStyle === "full"}
              onClick={() => {
                setCloseButtonStyle("full");
                saveCloseTableButtonStyle("full");
              }}
            >
              <RectangleHorizontal />
              <span>{t("Πλήρες")}</span>
            </button>
          </div>
          <p className="appearance-description">{t("Το τετράγωνο ✓ εμφανίζεται δίπλα στην εκτύπωση. Το πλήρες κουμπί εμφανίζεται από κάτω.")}</p>
        </section>

        <section className="settings-group appearance-wide">
          <h2><Pin aria-hidden="true" />{t("Σταθερά στοιχεία")}</h2>
          <p className="appearance-description">{t("Επιλέξτε ποια στοιχεία παραμένουν ορατά κατά την κύλιση.")}</p>
          <div className="appearance-pinned-controls">
            {STICKY_ROWS.map((row) => (
              <SettingsToggle key={row.key} label={t(row.label)} checked={sticky[row.key]} onChange={() => toggle(row.key)} />
            ))}
          </div>
        </section>
      </div>
    </section>
  );
}
